import { withDefaultGamificationMeta } from '@communities/common-layer/utils/gamification.utils'
import { communityUserQueryKeys } from './query-keys.utils'
import { groupsQueryKeys } from '../../groups-layer/utils/query-keys.utils'
import type { GroupMemberRole, MemberStatus } from '../../groups-layer/types/types'

import type { CommunityUserGroup } from '../types/community-user'
import type { QueryClient } from '@tanstack/vue-query'

type MembershipAnswersPayload = NonNullable<CommunityUserGroup['membershipAnswers']>

/**
 * True only when the payload has a non-empty `answers` array.
 * Empty shells like `{ formId, answers: [] }` must not count — they would
 * block preserving real answers already in cache after paid join.
 */
export function hasMembershipAnswerList(membershipAnswers: unknown): membershipAnswers is MembershipAnswersPayload {
  if (!membershipAnswers || typeof membershipAnswers !== 'object') return false
  const answers = (membershipAnswers as MembershipAnswersPayload).answers
  return Array.isArray(answers) && answers.length > 0
}

/**
 * Updates the community user query cache with new user data
 * This utility is stateless and can be reused across different mutations
 *
 * @param queryClient - TanStack Query client instance
 * @param groupId - Group ID for the query key
 * @param locationId - Location ID for the query key
 * @param updatedUserData - The updated user data to cache
 * @param options - Optional configuration
 * @returns The updated cached data or null if no existing data found
 */
export function updateCommunityUserCache(
  queryClient: QueryClient,
  groupId: string,
  locationId: string,
  updatedUserData: Partial<CommunityUserGroup>,
  options: {
    preserveExistingData?: boolean
    logPrefix?: string
    invalidateRelatedQueries?: boolean
  } = { preserveExistingData: true, logPrefix: '[CACHE-UPDATE]', invalidateRelatedQueries: true }
): CommunityUserGroup | null {
  const { preserveExistingData, logPrefix, invalidateRelatedQueries } = options

  try {
    // Get the query key for community user group details
    const communityUserQueryKey = communityUserQueryKeys.userGroupDetails(groupId, locationId)

    // Get existing cached data
    const existingData = (queryClient.getQueryData(communityUserQueryKey) ?? {}) as CommunityUserGroup

    // Merge the updated data with existing data. Keep prior membershipAnswers
    // when the patch omits them or only sends an empty shell.
    const mergedData: CommunityUserGroup = preserveExistingData
      ? {
          ...existingData,
          ...updatedUserData,
          membershipAnswers: hasMembershipAnswerList(updatedUserData.membershipAnswers)
            ? updatedUserData.membershipAnswers
            : (existingData.membershipAnswers ?? updatedUserData.membershipAnswers),
        }
      : (updatedUserData as CommunityUserGroup)

    // Update the cache with the merged data
    queryClient.setQueryData(communityUserQueryKey, mergedData)

    // Invalidate related queries if requested
    if (invalidateRelatedQueries) {
      // Invalidate group details to refresh member count
      queryClient.invalidateQueries({ queryKey: groupsQueryKeys.groupBySlug('') })
      // Invalidate group members if we have the group ID
      queryClient.invalidateQueries({ queryKey: groupsQueryKeys.groupMembers(groupId) })
    }

    return mergedData
  } catch (error) {
    console.error(`${logPrefix} Failed to update community user cache:`, error)
    throw error
  }
}

/**
 * Transforms API response data to CommunityUser format
 * This utility handles the common transformation logic for different API responses
 *
 * @param apiResponse - The API response data
 * @param existingData - Optional existing cached data to merge with
 * @returns Transformed CommunityUser data
 */
export function transformToCommunityUser(
  apiResponse: Record<string, unknown>,
  existingData?: CommunityUserGroup | null
): Partial<CommunityUserGroup> {
  // Do not default `membershipAnswers` to `{}` — that wiped real answers on
  // join/capture merges and hid the "Membership Answers" CTA after submit.
  // Empty shells `{ formId, answers: [] }` also must not count as answers.
  const membershipAnswersFromApi = apiResponse.membershipAnswers
  const hasMembershipAnswers = hasMembershipAnswerList(membershipAnswersFromApi)

  return {
    _id: apiResponse._id as string,
    contactId: apiResponse.contactId as string,
    groupId: apiResponse.groupId as string,
    locationId: apiResponse.locationId as string,
    role: apiResponse.role as GroupMemberRole,
    status: apiResponse.status as MemberStatus,
    ...(hasMembershipAnswers
      ? {
          membershipAnswers: membershipAnswersFromApi,
        }
      : {}),
    createdAt: apiResponse.createdAt as string,
    updatedAt: apiResponse.updatedAt as string,
    isChatDisabled: (apiResponse.isChatDisabled as boolean) || false,
    gamificationMeta: withDefaultGamificationMeta(
      apiResponse.gamificationMeta as { points?: number; level?: number; isLevelUp?: boolean } | undefined
    ),
    // Preserve existing data if provided
    ...(existingData && {
      lastVisitedAt: existingData.lastVisitedAt,
      lastActivityAt: existingData.lastActivityAt,
      ...(!hasMembershipAnswers && existingData.membershipAnswers ? { membershipAnswers: existingData.membershipAnswers } : {}),
    }),
  }
}

/**
 * Updates community user cache with group joining data
 * Specialized utility for group joining updates
 *
 * @param queryClient - TanStack Query client instance
 * @param groupId - Group ID for the query key
 * @param locationId - Location ID for the query key
 * @param joinResponse - The group joining API response
 * @returns The updated cached data or null if no existing data found
 */
